import { useChatHasDraft } from '@/components/chat/hooks/useChatInputDraft';
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipTrigger,
} from '@heidi/design-system/tooltip';
import { Pencil } from 'lucide-react';

interface ChatDraftIndicatorProps {
  chatId: string;
  /** Whether to show tooltip (desktop) or just title attribute (mobile) */
  showTooltip?: boolean;
  /** The currently active chat ID - indicator is hidden for the active chat */
  currentChatId?: string | null;
}

/**
 * Draft indicator component - shows a pencil icon when a chat has unsent draft text.
 * Similar to Slack's draft indicator in the channel list.
 * Only shows for chats that are NOT currently selected (you can see the draft in the input).
 */
export const ChatDraftIndicator = ({
  chatId,
  showTooltip = false,
  currentChatId,
}: ChatDraftIndicatorProps) => {
  const hasDraft = useChatHasDraft(chatId);

  // Don't show indicator for the currently active chat
  if (!hasDraft || chatId === currentChatId) return null;

  const icon = (
    <div
      className="shrink-0 text-text-secondary"
      aria-label="Has draft"
      title={showTooltip ? undefined : 'Draft'}
    >
      <Pencil size={12} />
    </div>
  );

  if (!showTooltip) return icon;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{icon}</TooltipTrigger>
      <TooltipContent side="right" sideOffset={8}>
        Draft
        <TooltipArrow />
      </TooltipContent>
    </Tooltip>
  );
};
